import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import b2bService from '../../services/b2bService';
import B2bPortalReportModal from './B2bPortalReportModal';
import './B2bPortalDashboardPage.css';

export const B2bPortalDashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected Report Modal State
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Self-Service Wallet Recharge Modal State
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(20000);
  const [rechargeMode, setRechargeMode] = useState('UPI');
  const [rechargeRef, setRechargeRef] = useState('');
  const [rechargeBank] = useState('HDFC Bank');
  const [rechargeNotes, setRechargeNotes] = useState('Portal payment / advance quota deposit');
  const [isSubmittingRecharge, setIsSubmittingRecharge] = useState(false);
  const [rechargeMsg, setRechargeMsg] = useState(null);

  // Auto-open payment modal if navigated with ?action=pay or ?pay=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'pay' || params.get('pay') === 'true') {
      const amt = parseFloat(params.get('amount'));
      if (amt && amt > 0) setRechargeAmount(amt);
      const inv = params.get('invoice');
      if (inv) setRechargeNotes(`Settlement for invoice ${inv}`);
      setIsRechargeOpen(true);
    }
  }, []);

  // last 30 days of orders, for the business overview
  const [monthOrders, setMonthOrders] = useState([]);
  const [topupHistory, setTopupHistory] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const day = 24 * 3600 * 1000;
      const [dashMetrics, orders, month, topups] = await Promise.all([
        b2bService.getPortalDashboard(),
        b2bService.getPortalOrders({ offset: 0, limit: 6 }),
        b2bService.getPortalOrders({
          fromDate: new Date(Date.now() - 29 * day).toISOString().slice(0, 10),
          toDate: new Date().toISOString().slice(0, 10),
          offset: 0,
          limit: 500
        }),
        b2bService.getPortalTopupHistory().catch(() => [])
      ]);
      setMetrics(dashMetrics);
      setRecentOrders(Array.isArray(orders) ? orders : []);
      setMonthOrders(Array.isArray(month) ? month : []);
      setTopupHistory(Array.isArray(topups) ? topups : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load partner dashboard metrics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleOpenReport = async (labNo) => {
    setReportLoading(true);
    try {
      const data = await b2bService.getPortalReportDetails(labNo);
      setSelectedReport(data);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to load test result details.');
    } finally {
      setReportLoading(false);
    }
  };

  const handleRechargeSubmit = async (e) => {
    e.preventDefault();
    const amountNum = parseFloat(rechargeAmount);
    if (!amountNum || amountNum <= 0) {
      setRechargeMsg({ type: 'error', text: 'Please enter a valid deposit amount.' });
      return;
    }
    const cleanRef = (rechargeRef || '').trim();
    if (!cleanRef || cleanRef.length < 4) {
      setRechargeMsg({ type: 'error', text: 'Valid UTR / Transaction Reference number is required (min 4 characters).' });
      return;
    }

    setIsSubmittingRecharge(true);
    setRechargeMsg(null);
    try {
      const res = await b2bService.submitPortalTopup({
        amount: amountNum,
        paymentMode: rechargeMode,
        transactionReference: cleanRef,
        bankName: rechargeBank.trim() || 'HDFC Bank Ltd',
        notes: rechargeNotes.trim() || 'Portal self-service wallet topup request'
      });
      const reqNum = res?.requestNumber || res?.RequestNumber || 'Topup Request';
      setRechargeMsg({
        type: 'success',
        text: `✓ Top-up Request #${reqNum} submitted! It has been routed to the accounts team for bank credit verification. Once verified, ₹${amountNum.toLocaleString('en-IN')} will be credited to your wallet.`
      });
      setRechargeRef('');
      setTimeout(() => {
        setIsRechargeOpen(false);
        fetchDashboardData();
      }, 2500);
    } catch (err) {
      setRechargeMsg({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Failed to submit wallet top-up request'
      });
    } finally {
      setIsSubmittingRecharge(false);
    }
  };

  const canSeeMoney = (() => {
    try { return !/viewer/i.test(JSON.parse(sessionStorage.getItem('b2b_portal_user') || '{}').role || ''); } catch { return true; }
  })();

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Metrics extraction & normalization
  const creditLimit = Number(metrics?.creditLimit ?? metrics?.CreditLimit ?? 0);
  const totalExposure = Number(metrics?.totalExposure ?? metrics?.TotalExposure ?? 0);
  const availableCredit = Number(metrics?.availableCredit ?? metrics?.AvailableCredit ?? 0);
  const unappliedAdvances = Number(metrics?.unappliedAdvances ?? metrics?.UnappliedAdvances ?? 0);
  const unbilledAmount = Number(metrics?.unbilledAmount ?? metrics?.UnbilledAmount ?? 0);
  const partyId = metrics?.partyId ?? metrics?.partyID ?? metrics?.PartyID ?? '';
  const partyName = metrics?.partyName ?? metrics?.PartyName ?? 'Corporate Partner';
  const creditPolicy = (metrics?.creditPolicy ?? metrics?.CreditPolicy ?? (creditLimit <= 0 ? 'PREPAID' : 'HYBRID')).toUpperCase();
  const isPrepaid = creditPolicy === 'PREPAID';
  const isDaysOnly = creditPolicy === 'DAYS_ONLY';
  const netPrepaidQuota = unappliedAdvances - unbilledAmount;

  // Quota & Exposure percentages
  const quotaUtilizationPct = useMemo(() => {
    if (isPrepaid) {
      if (unappliedAdvances <= 0) return 100;
      return Math.min(100, Math.max(0, Math.round((unbilledAmount / unappliedAdvances) * 100)));
    }
    if (creditLimit <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((totalExposure / creditLimit) * 100)));
  }, [isPrepaid, unappliedAdvances, unbilledAmount, creditLimit, totalExposure]);

  // Total orders & authorized count
  const totalOrdersCount = metrics?.totalBookingsCount ?? metrics?.totalOrdersMonth ?? recentOrders.length;
  const authorizedOrdersCount = recentOrders.filter((o) => (o.approvedTestCount || 0) > 0).length;

  const overview = useMemo(() => {
    const live = monthOrders.filter((o) => !o.isCancel);
    const spend = live.reduce((a, o) => a + Number(o.netAmount || 0), 0);
    const ready = live.filter((o) => (o.testCount || 0) > 0 && (o.approvedTestCount || 0) >= o.testCount).length;
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 3600 * 1000);
      days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), amount: 0, count: 0 });
    }
    live.forEach((o) => {
      const k = new Date(o.bookingDate).toISOString().slice(0, 10);
      const slot = days.find((x) => x.key === k);
      if (slot) { slot.amount += Number(o.netAmount || 0); slot.count += 1; }
    });
    return {
      spend,
      orders: live.length,
      ready,
      pending: live.length - ready,
      readyPct: live.length ? Math.round((ready / live.length) * 100) : 0,
      avgBill: live.length ? spend / live.length : 0,
      days,
      maxDay: Math.max(1, ...days.map((x) => x.amount))
    };
  }, [monthOrders]);

  if (loading && !metrics) {
    return (
      <div className="b2b-loading-box">
        <div className="b2b-loading-spinner" />
        <h3>Connecting to Laboratory Intelligence Core...</h3>
        <p>Retrieving audited ledger transactions, specimen pipeline, and contractual metrics.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="b2b-error-box">
        <span className="b2b-error-icon">⚠️</span>
        <h3 style={{ color: '#ef4444' }}>Error Loading Corporate Dashboard</h3>
        <p>{error}</p>
        <button className="b2b-refresh-btn primary" onClick={fetchDashboardData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="b2b-dashboard-container">
      {/* ── Executive Top Navigation Strip ── */}
      <div className="b2b-dashboard-header">
        <div>
          <div className="b2b-client-meta-strip">
            <span className={`b2b-status-pill ${isPrepaid ? (netPrepaidQuota > 0 ? 'pill-green' : 'pill-red') : (availableCredit > 0 ? 'pill-blue' : 'pill-red')}`}>
              ● {isPrepaid ? (netPrepaidQuota > 0 ? 'PREPAID WALLET ACTIVE' : 'QUOTA EXHAUSTED') : isDaysOnly ? 'CORPORATE 30-DAY TERM' : (availableCredit > 0 ? 'CREDIT LINE HEALTHY' : 'CREDIT CEILING BREACHED')}
            </span>
            <span className="b2b-meta-divider">•</span>
            <span className="b2b-meta-text">Client ID: <strong>#{partyId}</strong></span>
          </div>
          <h2 className="b2b-dashboard-title">{partyName}</h2>
          <div className="b2b-dashboard-subtitle">
            Executive Diagnostic Operations & Commercial Settlement Hub &bull; Direct Laboratory Node
          </div>
        </div>

        <div className="b2b-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canSeeMoney && (
            <button
              type="button"
              className="b2b-btn-topup b2b-btn-pay-main"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                fontSize: '13.5px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                cursor: 'pointer'
              }}
              onClick={() => {
                setRechargeMsg(null);
                if (totalExposure > 0 && !isPrepaid) {
                  setRechargeAmount(totalExposure);
                }
                setIsRechargeOpen(true);
              }}
            >
              <span>💳 Make Payment / Top-Up</span>
            </button>
          )}
          <button className="b2b-refresh-btn" onClick={fetchDashboardData} title="Sync real-time balances from lab database">
            🔄 Refresh Live Metrics
          </button>
        </div>
      </div>

      {/* ── Dynamic Flash Alert Governance Banner ── */}
      {(() => {
        if (!isPrepaid && !isDaysOnly && creditLimit > 0 && availableCredit <= 0) {
          return (
            <div className="b2b-flash-alert-banner critical">
              <div className="b2b-flash-alert-icon">🚨</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  CRITICAL NOTICE: Credit Limit Fully Exhausted (₹{formatCurrency(totalExposure)} / ₹{formatCurrency(creditLimit)})
                </div>
                <div className="b2b-flash-alert-desc">
                  Your laboratory account has reached its 100% approved credit exposure ceiling. Under multi-center policy, <strong>subsequent patient test bookings and final signed report release are currently on administrative hold</strong>. Please settle overdue invoices to unlock immediate intake.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <Link to="/b2b/portal/invoices" className="b2b-flash-btn primary">Pay / Settle Invoices</Link>
                <Link to="/b2b/portal/ledger" className="b2b-flash-btn outline">View Passbook</Link>
              </div>
            </div>
          );
        } else if (isPrepaid && netPrepaidQuota <= 0) {
          return (
            <div className="b2b-flash-alert-banner critical">
              <div className="b2b-flash-alert-icon">⛔</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  PREPAID QUOTA FULLY EXHAUSTED: Zero Usable Booking Balance
                </div>
                <div className="b2b-flash-alert-desc">
                  Your prepaid advance quota has been fully consumed by registered patient specimen accessions. In accordance with strict prepaid protocol, <strong>subsequent patient test bookings are temporarily on hold</strong> until an advance top-up is credited.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <button
                  type="button"
                  className="b2b-flash-btn primary"
                  onClick={() => {
                    setRechargeMsg(null);
                    setIsRechargeOpen(true);
                  }}
                >
                  ⚡ Instant Top-Up
                </button>
                <Link to="/b2b/portal/ledger" className="b2b-flash-btn outline">View Passbook</Link>
              </div>
            </div>
          );
        } else if (isDaysOnly) {
          return (
            <div className="b2b-flash-alert-banner info">
              <div className="b2b-flash-alert-icon">🏢</div>
              <div className="b2b-flash-alert-content">
                <div className="b2b-flash-alert-title">
                  CORPORATE WELLNESS AGREEMENT: 30-Day Periodic Billing Active
                </div>
                <div className="b2b-flash-alert-desc">
                  Employee medical checkups and health camp accessions are batched continuously. Current cycle has <strong>{totalOrdersCount} active employee registrations</strong> with ₹{formatCurrency(unbilledAmount)} in unbilled work-in-progress. Next consolidated tax invoice generates on month-end.
                </div>
              </div>
              <div className="b2b-flash-alert-actions">
                <Link to="/b2b/portal/orders" className="b2b-flash-btn primary">View Employee Roster</Link>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* ── Business overview: what the client spent and what is waiting ── */}
      <div className="b2b-overview">
        <div className="b2b-ov-kpis">
          <div className="b2b-ov-kpi tone-blue">
            <span className="b2b-ov-label">Spend, last 30 days</span>
            <strong className="b2b-ov-value">₹{formatCurrency(overview.spend)}</strong>
            <span className="b2b-ov-sub">{overview.orders} orders &bull; avg ₹{formatCurrency(overview.avgBill)} per order</span>
          </div>
          <div className="b2b-ov-kpi tone-amber">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="b2b-ov-label">{isPrepaid ? 'Used from advance (unbilled)' : 'Amount to pay'}</span>
              {canSeeMoney && (
                <button
                  type="button"
                  style={{
                    background: '#d97706',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setRechargeMsg(null);
                    setRechargeAmount(isPrepaid ? Math.max(5000, unbilledAmount) : (totalExposure || 10000));
                    setIsRechargeOpen(true);
                  }}
                >
                  Pay Now
                </button>
              )}
            </div>
            <strong className="b2b-ov-value">₹{formatCurrency(isPrepaid ? unbilledAmount : totalExposure)}</strong>
            <span className="b2b-ov-sub">Invoiced: ₹{formatCurrency(metrics?.invoicedReceivables ?? metrics?.InvoicedReceivables ?? 0)} &bull; Unbilled: ₹{formatCurrency(unbilledAmount)}</span>
          </div>
          <div className="b2b-ov-kpi tone-green">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="b2b-ov-label">{isPrepaid ? 'Advance balance left' : 'Credit still available'}</span>
              {canSeeMoney && (
                <button
                  type="button"
                  style={{
                    background: '#059669',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setRechargeMsg(null);
                    setRechargeAmount(20000);
                    setIsRechargeOpen(true);
                  }}
                >
                  + Top-Up
                </button>
              )}
            </div>
            <strong className="b2b-ov-value">₹{formatCurrency(isPrepaid ? netPrepaidQuota : availableCredit)}</strong>
            <span className="b2b-ov-sub">{isPrepaid ? 'Advance paid minus work done' : `Limit ₹${formatCurrency(creditLimit)}`}</span>
          </div>
          <div className="b2b-ov-kpi tone-violet">
            <span className="b2b-ov-label">Reports ready</span>
            <strong className="b2b-ov-value">{overview.readyPct}%</strong>
            <span className="b2b-ov-sub">{overview.ready} ready &bull; {overview.pending} waiting for approval</span>
          </div>
          <div className="b2b-ov-kpi tone-cyan">
            <span className="b2b-ov-label">Booked today</span>
            <strong className="b2b-ov-value">{metrics?.todayBookingsCount ?? metrics?.TodayBookingsCount ?? 0}</strong>
            <span className="b2b-ov-sub">{metrics?.outstandingInvoicesCount ?? metrics?.OutstandingInvoicesCount ?? 0} invoices open</span>
          </div>
        </div>

        <div className="b2b-ov-chart">
          <div className="b2b-ov-chart-head">
            <strong>Daily spend, last 14 days</strong>
            <span>₹{formatCurrency(overview.days.reduce((a, d) => a + d.amount, 0))} in total</span>
          </div>
          <div className="b2b-ov-bars" role="img" aria-label="Daily spend for the last 14 days">
            {overview.days.map((d) => (
              <div key={d.key} className="b2b-ov-bar-col" title={`${d.label}: ₹${formatCurrency(d.amount)} (${d.count} orders)`}>
                <div className="b2b-ov-bar" style={{ height: `${Math.max(4, (d.amount / overview.maxDay) * 100)}%` }} />
                <span className="b2b-ov-bar-label">{d.label.slice(0, 2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Executive KPI Cards (World-Class Aesthetics) ── */}
      <div className="b2b-metrics-grid">
        {isPrepaid ? (
          <>
            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Prepaid Usable Quota</span>
                <span className="b2b-metric-chip green">Live Balance</span>
              </div>
              <div className={`b2b-metric-value currency ${netPrepaidQuota > 2000 ? 'positive' : netPrepaidQuota > 0 ? 'warning' : 'critical'}`}>
                {formatCurrency(netPrepaidQuota)}
              </div>
              <div className="b2b-progress-bar-wrap">
                <div
                  className="b2b-progress-bar-fill green"
                  style={{ width: `${100 - quotaUtilizationPct}%` }}
                />
              </div>
              <div className="b2b-metric-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{100 - quotaUtilizationPct}% available</span>
                <button
                  type="button"
                  style={{
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '3px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setRechargeMsg(null);
                    setIsRechargeOpen(true);
                  }}
                >
                  ⚡ Recharge Quota
                </button>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Total Advances Credited</span>
                <span className="b2b-metric-chip neutral">Advance</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(unappliedAdvances)}
              </div>
              <span className="b2b-metric-meta">Cumulative Unapplied Deposits</span>
              <div className="b2b-metric-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>See Financial Ledger for receipts</span>
                <button
                  type="button"
                  style={{
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '3px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setRechargeMsg(null);
                    setIsRechargeOpen(true);
                  }}
                >
                  + Add Funds
                </button>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-amber">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Current Cycle Consumption</span>
                <span className="b2b-metric-chip amber">In Progress</span>
              </div>
              <div className="b2b-metric-value currency warning">
                {formatCurrency(unbilledAmount)}
              </div>
              <span className="b2b-metric-meta">Unbilled Accessions Consumed</span>
              <div className="b2b-metric-footer">
                <span>Deducted automatically from quota</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Registered Specimens</span>
                <span className="b2b-metric-chip blue">Registered</span>
              </div>
              <div className="b2b-metric-value info">
                {totalOrdersCount}
              </div>
              <span className="b2b-metric-meta">{authorizedOrdersCount} Authorized &bull; {totalOrdersCount - authorizedOrdersCount} In Analysis</span>
              <div className="b2b-metric-footer">
                <span>Orders registered under your account</span>
              </div>
            </div>
          </>
        ) : isDaysOnly ? (
          <>
            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Unbilled Work In Progress</span>
                <span className="b2b-metric-chip blue">Current Cycle</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(unbilledAmount)}
              </div>
              <span className="b2b-metric-meta">Corporate Camp Accessions Accrued</span>
              <div className="b2b-metric-footer">
                <span>Terms: 30 Days Net from Invoice</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Corporate Employee Count</span>
                <span className="b2b-metric-chip green">100% Tested</span>
              </div>
              <div className="b2b-metric-value positive">
                {totalOrdersCount}
              </div>
              <span className="b2b-metric-meta">Active Wellness Examinations</span>
              <div className="b2b-metric-footer">
                <span>All reports MD authorized</span>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Billing Cycle</span>
                <span className="b2b-metric-chip neutral">Monthly</span>
              </div>
              <div className="b2b-metric-value" style={{ fontSize: '20px' }}>
                Oct 01 &ndash; Oct 31
              </div>
              <span className="b2b-metric-meta">Consolidated Statement Closes Oct 31</span>
              <div className="b2b-metric-footer">
                <span>Auto-dispatched via Email & Portal</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="b2b-metric-card card-accent-blue">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Approved Credit Ceiling</span>
                <span className="b2b-metric-chip neutral">Contractual</span>
              </div>
              <div className="b2b-metric-value currency">
                {formatCurrency(creditLimit)}
              </div>
              <span className="b2b-metric-meta">Negotiated Institutional Limit</span>
              <div className="b2b-metric-footer">
                <span>Billed at your contract rates</span>
              </div>
            </div>

            <div className="b2b-metric-card card-accent-amber">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Current Total Exposure</span>
                <span className={`b2b-metric-chip ${quotaUtilizationPct >= 90 ? 'critical' : 'amber'}`}>
                  {quotaUtilizationPct}% Consumed
                </span>
              </div>
              <div className="b2b-metric-value currency warning">
                {formatCurrency(totalExposure)}
              </div>
              <div className="b2b-progress-bar-wrap">
                <div
                  className={`b2b-progress-bar-fill ${quotaUtilizationPct >= 90 ? 'critical' : 'amber'}`}
                  style={{ width: `${quotaUtilizationPct}%` }}
                />
              </div>
              <div className="b2b-metric-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Unbilled: ₹{formatCurrency(unbilledAmount)}</span>
                {canSeeMoney && (
                  <button
                    type="button"
                    style={{
                      background: '#d97706',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '5px',
                      padding: '4px 11px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setRechargeMsg(null);
                      setRechargeAmount(totalExposure || 10000);
                      setIsRechargeOpen(true);
                    }}
                  >
                    💳 Settle / Pay Now
                  </button>
                )}
              </div>
            </div>

            <div className="b2b-metric-card card-accent-green">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Available Booking Headroom</span>
                <span className="b2b-metric-chip green">Instant Access</span>
              </div>
              <div className={`b2b-metric-value currency ${availableCredit > 0 ? 'positive' : 'critical'}`}>
                {formatCurrency(availableCredit)}
              </div>
              <span className="b2b-metric-meta">Unrestricted Specimen Registration</span>
              <div className="b2b-metric-footer">
                <span>{availableCredit > 0 ? 'Intake Gateway Active' : 'Intake On Administrative Hold'}</span>
              </div>
            </div>

            <div className="b2b-metric-card">
              <div className="b2b-metric-header">
                <span className="b2b-metric-label">Invoices Awaiting Settlement</span>
                <span className="b2b-metric-chip neutral">Reconciliation</span>
              </div>
              <div className="b2b-metric-value info">
                {metrics?.outstandingInvoicesCount ?? (metrics?.pendingInvoicesCount || 0)}
              </div>
              <span className="b2b-metric-meta">Invoiced: ₹{formatCurrency(metrics?.invoicedReceivables)}</span>
              <div className="b2b-metric-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link to="/b2b/portal/invoices" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                  Review Invoices &rarr;
                </Link>
                {canSeeMoney && (
                  <button
                    type="button"
                    style={{
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '5px',
                      padding: '4px 11px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setRechargeMsg(null);
                      const invAmt = Number(metrics?.invoicedReceivables) || 10000;
                      setRechargeAmount(invAmt);
                      setIsRechargeOpen(true);
                    }}
                  >
                    💳 Pay Invoices
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Live Accessions Tracker & Quick Actions ── */}
      <div className="b2b-dashboard-content">
        {/* Left: Recent Accessions Feed with 1-Click Clinical Reports */}
        <div className="b2b-card" style={{ gridColumn: 'span 1' }}>
          <div className="b2b-card-header-flex">
            <div>
              <h3 className="b2b-card-title" style={{ border: 'none', margin: 0, padding: 0 }}>
                Recent Patient Accessions &amp; Clinical Reports
              </h3>
              <p className="b2b-card-subtitle">Real-time accession pipeline bound to your corporate account</p>
            </div>
            <Link to="/b2b/portal/orders" className="b2b-view-all-link">
              View All Accessions &rarr;
            </Link>
          </div>

          {recentOrders.length === 0 ? (
            <div className="b2b-empty-state-box">
              <span style={{ fontSize: '36px' }}>📋</span>
              <p>No recent accessions registered in this period.</p>
            </div>
          ) : (
            <div className="b2b-table-responsive">
              <table className="b2b-data-table">
                <thead>
                  <tr>
                    <th>Patient Details</th>
                    <th>Lab Transaction #</th>
                    <th>Date / Time</th>
                    <th>Gross / Net</th>
                    <th>Test Status</th>
                    <th style={{ textAlign: 'center' }}>Clinical Report</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((ord) => {
                    const isFullyApproved = (ord.approvedTestCount || 0) >= (ord.testCount || 1);
                    return (
                      <tr key={ord.ledgerTransactionID || ord.ledgerTransactionNo}>
                        <td>
                          <div className="b2b-patient-cell">
                            <span className="b2b-patient-avatar">
                              {ord.patientName ? ord.patientName.charAt(0).toUpperCase() : 'P'}
                            </span>
                            <div>
                              <strong className="b2b-pname">{ord.patientName}</strong>
                              <span className="b2b-pdemog">
                                {ord.gender || '—'} &bull; {ord.ageYear ? `${ord.ageYear}Y` : '—'} &bull; UHID: {ord.uhid || ord.patient_ID}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong className="font-mono" style={{ color: '#0284c7' }}>
                            {ord.ledgerTransactionNo}
                          </strong>
                        </td>
                        <td>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {ord.bookingDate ? new Date(ord.bookingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: '#0f172a' }}>₹{formatCurrency(ord.netAmount)}</strong>
                        </td>
                        <td>
                          <span className={`b2b-badge ${isFullyApproved ? 'completed' : 'pending'}`}>
                            {isFullyApproved ? '✓ Ready / Authorized' : '⏳ In Analysis'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {isFullyApproved ? (
                            <button
                              type="button"
                              className="b2b-btn-report-view"
                              onClick={() => handleOpenReport(ord.ledgerTransactionNo)}
                              disabled={reportLoading}
                            >
                              👁️ View Report
                            </button>
                          ) : (
                            <span className="b2b-report-locked" title="Report opens after the pathologist approves every test">
                              🔒 Awaiting approval
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Top-up Requests History Strip */}
          {topupHistory.length > 0 && (
            <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  💳 Recent Wallet Top-Up Requests
                </span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {topupHistory.filter(t => t.status === 'PENDING').length} Pending Verification
                </span>
              </div>
              <div className="b2b-table-responsive">
                <table className="b2b-data-table" style={{ fontSize: '12.5px' }}>
                  <thead>
                    <tr>
                      <th>Request #</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Channel</th>
                      <th>UTR / Reference</th>
                      <th>Verification Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topupHistory.slice(0, 5).map((t) => (
                      <tr key={t.requestId}>
                        <td style={{ fontWeight: 600, color: '#2563eb' }}>{t.requestNumber}</td>
                        <td>{new Date(t.requestedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                        <td style={{ fontWeight: 700, color: '#16a34a' }}>₹{formatCurrency(t.requestedAmount)}</td>
                        <td>{t.paymentMode}</td>
                        <td style={{ fontFamily: 'monospace' }}>{t.transactionReference}</td>
                        <td>
                          {t.status === 'APPROVED' ? (
                            <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '4px', fontWeight: 600, fontSize: '11.5px' }}>
                              ✓ Credited ({t.advanceReceiptNumber || 'ADV'})
                            </span>
                          ) : t.status === 'REJECTED' ? (
                            <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '3px 8px', borderRadius: '4px', fontWeight: 600, fontSize: '11.5px' }} title={t.reviewRemarks}>
                              ✕ Rejected
                            </span>
                          ) : (
                            <span style={{ background: '#fef9c3', color: '#854d0e', padding: '3px 8px', borderRadius: '4px', fontWeight: 600, fontSize: '11.5px' }}>
                              ⏳ Pending Verification
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right: Quick Actions & Financial Navigation */}
        <div className="b2b-card">
          <h3 className="b2b-card-title">Portal Quick Operations</h3>
          <div className="b2b-quick-actions">
            {canSeeMoney && (
              <button
                type="button"
                className="b2b-action-item action-highlight"
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: '8px'
                }}
                onClick={() => {
                  setRechargeMsg(null);
                  if (!isPrepaid && totalExposure > 0) {
                    setRechargeAmount(totalExposure);
                  }
                  setIsRechargeOpen(true);
                }}
              >
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 700 }}>💳 Make Payment / Wallet Top-Up</div>
                  <div style={{ fontSize: '11px', opacity: 0.9, fontWeight: 400 }}>Settle outstanding dues or deposit advance</div>
                </div>
                <span style={{ fontSize: '16px' }}>&rarr;</span>
              </button>
            )}

            <Link to="/b2b/portal/orders" className="b2b-action-item">
              <span>📋 Search Patient Orders &amp; Results</span>
              <span>&rarr;</span>
            </Link>

            {canSeeMoney && <Link to="/b2b/portal/invoices" className="b2b-action-item">
              <span>🧾 Invoices &amp; Reconciliation</span>
              <span>&rarr;</span>
            </Link>}

            {canSeeMoney && <Link to="/b2b/portal/ledger" className="b2b-action-item">
              <span>📜 Download Financial Passbook / Statement</span>
              <span>&rarr;</span>
            </Link>}
          </div>

          <div className="b2b-support-box">
            <span className="b2b-support-title">Need Priority Laboratory Support?</span>
            <p className="b2b-support-desc">
              Dedicated B2B Desk: <strong>+91 (011) 4000-8899</strong><br />
              Email: <strong>b2b-support@mylis.enterprise.in</strong>
            </p>
          </div>
        </div>
      </div>

      {selectedReport && (
        <B2bPortalReportModal report={selectedReport} onClose={() => setSelectedReport(null)} />
      )}

      {/* ── Self-Service Wallet Recharge & Due Settlement Modal ── */}
      {isRechargeOpen && (
        <div className="b2b-pay-modal-overlay" onClick={() => !isSubmittingRecharge && setIsRechargeOpen(false)}>
          <div className="b2b-pay-modal-card" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="b2b-pay-header">
              <div className="b2b-pay-title-wrap">
                <div className="b2b-pay-icon-badge">💳</div>
                <div>
                  <h3 className="b2b-pay-title">Submit Payment / Wallet Top-Up</h3>
                  <div className="b2b-pay-subtitle">Direct bank transfer clearance &amp; quota recharge</div>
                </div>
              </div>
              <button
                type="button"
                className="b2b-pay-close-btn"
                onClick={() => setIsRechargeOpen(false)}
                disabled={isSubmittingRecharge}
                title="Close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleRechargeSubmit}>
              <div className="b2b-pay-body">
                {rechargeMsg && (
                  <div className={`b2b-msg-alert ${rechargeMsg.type}`} style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '13px', lineHeight: 1.5 }}>
                    {rechargeMsg.text}
                  </div>
                )}

                {/* Laboratory Bank & UPI Coordinates Card */}
                <div className="b2b-bank-coords-card">
                  <div className="b2b-bank-coords-header">
                    <span className="b2b-bank-coords-title">
                      <span>🏦</span> Laboratory Official Settlement Account
                    </span>
                    <span className="b2b-bank-verified-tag">✓ Verified Account</span>
                  </div>
                  <div className="b2b-bank-coords-grid">
                    <div className="b2b-bank-item">
                      Bank Name: <strong>HDFC Bank Ltd</strong>
                    </div>
                    <div className="b2b-bank-item">
                      Account #: <strong>50200088991122</strong>
                    </div>
                    <div className="b2b-bank-item">
                      IFSC Code: <strong>HDFC0000123</strong>
                    </div>
                    <div className="b2b-bank-item">
                      UPI ID: <strong style={{ color: '#059669' }}>lab.enterprise@hdfcbank</strong>
                    </div>
                  </div>
                  <div className="b2b-bank-coords-tip">
                    <span>💡</span> Transfer via any UPI app or Net Banking, then enter the 12-digit UTR below for accounts clearance.
                  </div>
                </div>

                {/* Quick Amount Select */}
                <div className="b2b-pay-slabs-wrap">
                  <span className="b2b-pay-section-label">Quick Amount Select</span>
                  <div className="b2b-pay-slabs-grid">
                    {totalExposure > 0 && !isPrepaid && (
                      <button
                        type="button"
                        className={`b2b-pay-slab-pill due-btn ${rechargeAmount === totalExposure ? 'selected' : ''}`}
                        onClick={() => setRechargeAmount(totalExposure)}
                      >
                        ⚡ Clear Total Due (₹{formatCurrency(totalExposure)})
                      </button>
                    )}
                    {Number(metrics?.invoicedReceivables || 0) > 0 && (
                      <button
                        type="button"
                        className={`b2b-pay-slab-pill inv-btn ${rechargeAmount === Number(metrics?.invoicedReceivables) ? 'selected' : ''}`}
                        onClick={() => setRechargeAmount(Number(metrics?.invoicedReceivables))}
                      >
                        🧾 Pay Invoices (₹{formatCurrency(metrics?.invoicedReceivables)})
                      </button>
                    )}
                    {[5000, 10000, 20000, 50000].map((amt) => (
                      <button
                        type="button"
                        key={amt}
                        className={`b2b-pay-slab-pill ${rechargeAmount === amt ? 'selected' : ''}`}
                        onClick={() => setRechargeAmount(amt)}
                      >
                        ₹{amt.toLocaleString('en-IN')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Amount Input */}
                <div className="b2b-pay-field">
                  <label className="b2b-pay-section-label">
                    Payment Amount (₹) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div className="b2b-pay-input-wrap">
                    <span className="b2b-pay-curr-symbol">₹</span>
                    <input
                      type="number"
                      min="100"
                      step="100"
                      className="b2b-pay-input"
                      value={rechargeAmount}
                      onChange={(e) => setRechargeAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Payment Channel Selection */}
                <div className="b2b-pay-field">
                  <label className="b2b-pay-section-label">
                    Payment Channel <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div className="b2b-channel-cards-grid">
                    {[
                      { id: 'UPI', icon: '📱', name: 'Instant UPI / QR' },
                      { id: 'NEFT', icon: '🏦', name: 'Bank NEFT' },
                      { id: 'RTGS', icon: '🏛️', name: 'RTGS Transfer' },
                      { id: 'IMPS', icon: '⚡', name: 'IMPS Fast' }
                    ].map((m) => (
                      <div
                        key={m.id}
                        className={`b2b-channel-card ${rechargeMode === m.id ? 'active' : ''}`}
                        onClick={() => setRechargeMode(m.id)}
                      >
                        <span className="b2b-channel-icon">{m.icon}</span>
                        <span className="b2b-channel-name">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bank Reference / UTR Number */}
                <div className="b2b-pay-field">
                  <label className="b2b-pay-section-label">
                    Bank Reference / UTR Number <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="b2b-pay-text-input"
                    placeholder="e.g. 12-digit UTR or UPI Transaction Reference ID"
                    value={rechargeRef}
                    onChange={(e) => setRechargeRef(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Mandatory: The UTR is verified against bank statement credits.
                  </span>
                </div>

                {/* Remarks / Purpose */}
                <div className="b2b-pay-field">
                  <label className="b2b-pay-section-label">Payment Purpose / Remarks (Optional)</label>
                  <input
                    type="text"
                    className="b2b-pay-text-input"
                    placeholder="e.g. Settle Invoice INV/2026/01 or November advance deposit"
                    value={rechargeNotes}
                    onChange={(e) => setRechargeNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="b2b-pay-footer">
                <button
                  type="button"
                  className="b2b-pay-btn-cancel"
                  onClick={() => setIsRechargeOpen(false)}
                  disabled={isSubmittingRecharge}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="b2b-pay-btn-submit"
                  disabled={isSubmittingRecharge}
                >
                  {isSubmittingRecharge ? 'Submitting Verification...' : `Submit Payment Verification (₹${Number(rechargeAmount || 0).toLocaleString('en-IN')})`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bPortalDashboardPage;
